import axios from 'axios';
import { API_BASE_URL } from '../config/constants';
import { CookieService } from './cookieService';

export interface LoginResponse {
  token: string;
  user: {
    id: string;
    username: string;
    email?: string;
  };
}

export interface LoginOptions {
  rememberMe?: boolean;
  returnUrl?: string;
}

export interface AuthUser {
  id: string;
  username: string;
  email?: string;
  isAdmin?: boolean;
}

export class AuthService {
  private static readonly BASE_URL = `${API_BASE_URL}/api/auth`;

  static async login(username: string, password: string, options: LoginOptions = {}): Promise<LoginResponse> {
    try {
      const response = await axios.post(`${this.BASE_URL}/login`, {
        username,
        password,
      });
      
      console.log('Backend login response:', response.data);
      
      // Handle the actual backend response structure
      const data = response.data;
      
      // Backend only returns token, so we create a minimal user object for immediate use
      if (data.token) {
        const loginResponse = {
          token: data.token,
          user: {
            id: '1',
            username: username, // Use the provided username since backend doesn't return it
            email: `${username}@compassai.com`
          }
        };
        
        // Store token in localStorage for immediate use
        localStorage.setItem('authToken', data.token);
        
        // If remember me is enabled, also store in cookies
        if (options.rememberMe) {
          console.log('Setting "Remember me" cookies for 30 days');
          CookieService.setCookie('authToken', data.token, 30); // 30 days
          CookieService.setCookie('rememberMe', 'true', 30);
          
          // Store current theme preferences
          const themeConfig = localStorage.getItem('compassai-theme-config');
          if (themeConfig) {
            CookieService.setCookie('themeConfig', themeConfig, 30);
          }
          
          // Store current color mode from Chakra UI
          const colorMode = localStorage.getItem('chakra-ui-color-mode');
          if (colorMode) {
            CookieService.setCookie('colorMode', colorMode, 30);
          }
          
          console.log('Cookies set successfully for remember me');
        } else {
          console.log('Remember me not enabled, only using localStorage');
        }
        
        return loginResponse;
      }
      
      throw new Error('No token received from server');
    } catch (error: any) {
      console.error('Login error:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      if (error.response?.status === 401) {
        throw new Error('Invalid username or password');
      }
      if (error.message.includes('Network Error')) {
        throw new Error('Unable to connect to server. Please check if the backend is running.');
      }
      throw new Error('Login failed. Please try again.');
    }
  }

  static async verifyToken(): Promise<AuthUser> {
    try {
      let token = localStorage.getItem('authToken');
      
      // If no token in localStorage, check cookies
      if (!token) {
        token = CookieService.getCookie('authToken');
        if (token) {
          // Restore token to localStorage from cookie
          localStorage.setItem('authToken', token);
        }
      }
      
      if (!token) {
        throw new Error('No token found');
      }

      try {
        // Try to verify token with backend
        const response = await axios.get(`${this.BASE_URL}/verify`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const userData = response.data.user;
        
        // Store the verified user data
        localStorage.setItem('authUser', JSON.stringify(userData));
        if (CookieService.getCookie('authToken')) {
          CookieService.setCookie('authUser', JSON.stringify(userData), 30);
        }

        return userData;
      } catch (verifyError: any) {
        // If backend verification fails, try to use stored user data as fallback
        // This prevents users from being logged out due to network issues
        console.warn('Backend token verification failed, using stored data:', verifyError.message);
        
        const storedUser = this.getStoredUser();
        if (storedUser && token) {
          // Check if token is not expired using the helper method
          if (!this.isTokenExpired()) {
            // Token appears valid, return stored user data
            return storedUser;
          } else {
            console.warn('Token is expired, clearing auth data');
            this.clearAuthData();
            throw new Error('Token expired');
          }
        }
        
        // If all fallbacks fail, clear auth data and throw error
        this.clearAuthData();
        throw new Error('Token verification failed');
      }
    } catch (error: any) {
      // Clear both localStorage and cookies on verification failure
      this.clearAuthData();
      throw new Error('Token verification failed');
    }
  }

  static async resetPassword(email: string): Promise<void> {
    try {
      await axios.post(`${this.BASE_URL}/reset-password`, { email });
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Password reset failed. Please try again.');
    }
  }

  static async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      await axios.post(
        `${this.BASE_URL}/change-password`,
        {
          currentPassword,
          newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Password change failed. Please try again.');
    }
  }

  static isAuthenticated(): boolean {
    return !!localStorage.getItem('authToken') || !!CookieService.getCookie('authToken');
  }

  static getToken(): string | null {
    return localStorage.getItem('authToken') || CookieService.getCookie('authToken');
  }

  static logout(): void {
    this.clearAuthData();
  }
  
  /**
   * Clear all authentication data from localStorage and cookies
   */
  private static clearAuthData(): void {
    // Clear localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    
    // Clear cookies
    CookieService.deleteCookie('authToken');
    CookieService.deleteCookie('authUser');
    CookieService.deleteCookie('rememberMe');
    CookieService.deleteCookie('themeConfig');
    CookieService.deleteCookie('colorMode');
  }
  
  /**
   * Get stored user data
   */
  static getStoredUser(): AuthUser | null {
    try {
      let userStr = localStorage.getItem('authUser');
      if (!userStr) {
        userStr = CookieService.getCookie('authUser');
      }
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      return null;
    }
  }
  
  /**
   * Check if user has remember me enabled
   */
  static hasRememberMe(): boolean {
    return CookieService.getCookie('rememberMe') === 'true';
  }

  /**
   * Check if the current token is expired without making a backend call
   * @returns true if token is expired or invalid, false if valid
   */
  static isTokenExpired(): boolean {
    try {
      const token = this.getToken();
      if (!token) return true;

      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return true;

      const payload = JSON.parse(atob(tokenParts[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      return payload.exp && payload.exp < currentTime;
    } catch (error) {
      console.warn('Failed to check token expiration:', error);
      return true;
    }
  }

  /**
   * Check if token will expire soon (within 1 hour)
   * @returns true if token expires within 1 hour
   */
  static isTokenExpiringSoon(): boolean {
    try {
      const token = this.getToken();
      if (!token) return true;

      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return true;

      const payload = JSON.parse(atob(tokenParts[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      const oneHour = 60 * 60; // 1 hour in seconds
      
      return payload.exp && (payload.exp - currentTime) < oneHour;
    } catch (error) {
      console.warn('Failed to check if token expires soon:', error);
      return true;
    }
  }
  
  /**
   * Get stored theme preferences
   */
  static getStoredThemePreferences(): { themeConfig: string | null; colorMode: string | null } {
    return {
      themeConfig: CookieService.getCookie('themeConfig'),
      colorMode: CookieService.getCookie('colorMode')
    };
  }
  
  /**
   * Restore authentication from cookies if available
   */
  static restoreFromCookies(): boolean {
    const token = CookieService.getCookie('authToken');
    const userStr = CookieService.getCookie('authUser');
    const rememberMe = CookieService.getCookie('rememberMe');
    
    console.log('Attempting to restore from cookies:', { 
      hasToken: !!token, 
      hasUser: !!userStr, 
      rememberMe: rememberMe 
    });
    
    if (token && userStr) {
      localStorage.setItem('authToken', token);
      localStorage.setItem('authUser', userStr);
      
      // Restore theme preferences if they exist
      const themeConfig = CookieService.getCookie('themeConfig');
      if (themeConfig) {
        localStorage.setItem('compassai-theme-config', themeConfig);
      }
      
      const colorMode = CookieService.getCookie('colorMode');
      if (colorMode) {
        localStorage.setItem('chakra-ui-color-mode', colorMode);
      }
      
      console.log('Successfully restored authentication from cookies');
      return true;
    }
    
    console.log('No valid cookies found for restoration');
    return false;
  }
}